import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { FaFilter, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import DepartmentSelector from '../common/DepartmentSelector';
import EmployeeSelector from '../attendance/calendar/EmployeeSelector';

const FilterPanel = ({ filters, onFilterChange, onApplyFilters, onClearFilters, reportType }) => {
    const { user } = useAuth();
    // Check if user is admin/hr - they can see all employees
    const isAdmin = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'superadmin';
    
    const handleInputChange = (field, value) => {
        onFilterChange({
            ...filters,
            [field]: value
        });
    };

    const handleEmployeeChange = (ids) => {
        onFilterChange({
            ...filters,
            employeeId: Array.isArray(ids) ? ids.join(',') : ids
        });
    };

    const validateDateRange = () => {
        if (filters.startDate && filters.endDate) {
            return new Date(filters.startDate) <= new Date(filters.endDate);
        }
        return true;
    };

    const canSelectEmployees = isAdmin || user?.role === 'dept_head' || user?.role === 'production_head';

    // Show different filters based on report type
    const showEmployeeFilters = canSelectEmployees && !['department-wise'].includes(reportType);
    const showDateRange = !['loan-advance', 'department-wise', 'attendance', 'payroll'].includes(reportType);
    const showMonthFilter = ['payroll', 'attendance', 'department-wise'].includes(reportType);

    // Parse month/year for EmployeeSelector
    const [year, month] = (filters.month || '').split('-').map(Number);

    return (
        <Card className="shadow-sm border-0 rounded-4 overflow-visible">
            <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-black text-dark">
                    <FaFilter className="me-2 text-primary" />
                    Advanced Data Filters
                </h6>
                <Button
                    variant="link"
                    size="sm"
                    className="text-muted text-decoration-none fw-bold"
                    onClick={onClearFilters}
                >
                    <FaTimes className="me-1" />
                    Reset
                </Button>
            </Card.Header>
            <Card.Body className="px-4 pb-4 pt-1">
                <Row className="g-3">
                    {showMonthFilter && (
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small fw-black text-muted text-uppercase mb-2">
                                    <FaCalendarAlt className="me-1" /> Reporting Month
                                </Form.Label>
                                <Form.Control
                                    type="month"
                                    className="rounded-3 border-0 bg-light fw-bold"
                                    style={{ height: '48px' }}
                                    value={filters.month}
                                    onChange={(e) => handleInputChange('month', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    )}

                    {showEmployeeFilters && (
                        <Col md={5}>
                            <Form.Group>
                                <Form.Label className="small fw-black text-muted text-uppercase mb-2">Staff Selection</Form.Label>
                                <EmployeeSelector
                                    selectedEmployeeIds={filters.employeeId ? filters.employeeId.split(',') : []}
                                    onEmployeeChange={handleEmployeeChange}
                                    month={month}
                                    year={year}
                                    isMultiSelect={true}
                                />
                            </Form.Group>
                        </Col>
                    )}

                    <Col md={4}>
                        <Form.Group>
                            <Form.Label className="small fw-black text-muted text-uppercase mb-2">Organizational Unit</Form.Label>
                            <DepartmentSelector
                                value={filters.department}
                                onChange={(value) => handleInputChange('department', value)}
                                includeAll={true}
                                activeOnly={true}
                                className="rounded-3 border-0 bg-light fw-bold"
                                style={{ height: '48px' }}
                            />
                        </Form.Group>
                    </Col>

                    {showDateRange && (
                        <>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-black text-muted text-uppercase mb-2">Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        className="rounded-3 border-0 bg-light fw-bold"
                                        style={{ height: '48px' }}
                                        value={filters.startDate}
                                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                                        max={filters.endDate || undefined}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-black text-muted text-uppercase mb-2">End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        className="rounded-3 border-0 bg-light fw-bold"
                                        style={{ height: '48px' }}
                                        value={filters.endDate}
                                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                                        min={filters.startDate || undefined}
                                        isInvalid={!validateDateRange()}
                                    />
                                    {!validateDateRange() && (
                                        <Form.Control.Feedback type="invalid">
                                            End date must be after start date
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </>
                    )}

                    <Col md={canSelectEmployees ? 12 : 3} className="d-flex align-items-end justify-content-end">
                        <Button
                            variant="primary"
                            className="rounded-pill px-5 fw-black shadow-sm transition-all"
                            style={{ height: '48px' }}
                            onClick={onApplyFilters}
                            disabled={!validateDateRange()}
                        >
                            <FaFilter className="me-2" />
                            Run Analysis
                        </Button>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default FilterPanel;
